import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { confirmacionesAPI } from "@/lib/api";
import { ArrowLeft, Save, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function ConfirmacionFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    numero_libro: "",
    numero_folio: "",
    numero_partida: "",
    fecha_confirmacion: "",
    lugar_confirmacion: "",
    confirmado_nombre: "",
    confirmado_apellido: "",
    confirmado_fecha_nacimiento: "",
    confirmado_lugar_nacimiento: "",
    confirmado_genero: "masculino",
    padre_nombre: "",
    madre_nombre: "",
    padrino_nombre: "",
    madrina_nombre: "",
    obispo_nombre: "",
    parroquia_bautizo: "",
    fecha_bautizo: "",
    notas_marginales: "",
  });

  useEffect(() => {
    if (isEditing) {
      loadConfirmacion();
    }
  }, [id]);

  const loadConfirmacion = async () => {
    try {
      const response = await confirmacionesAPI.getOne(id);
      setFormData(response.data);
    } catch (error) {
      toast.error("Error al cargar la confirmación");
      navigate("/confirmaciones");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditing) {
        await confirmacionesAPI.update(id, formData);
        toast.success("Confirmación actualizada correctamente");
      } else {
        await confirmacionesAPI.create(formData);
        toast.success("Confirmación registrada correctamente");
      }
      navigate("/confirmaciones");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar la confirmación");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="confirmacion-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/confirmaciones")}
          className="h-10 w-10"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
            <Flame className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
            {isEditing ? "Editar Confirmación" : "Nueva Confirmación"}
          </h1>
          <p className="text-stone-600 mt-1">
            {isEditing ? "Modifica los datos del registro" : "Ingresa los datos de la nueva confirmación"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Datos del Registro */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Datos del Registro
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="numero_libro">Número de Libro *</Label>
              <Input
                id="numero_libro"
                name="numero_libro"
                value={formData.numero_libro}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="numero_folio">Número de Folio *</Label>
              <Input
                id="numero_folio"
                name="numero_folio"
                value={formData.numero_folio}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="numero_partida">Número de Partida *</Label>
              <Input
                id="numero_partida"
                name="numero_partida"
                value={formData.numero_partida}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fecha_confirmacion">Fecha de la Confirmación *</Label>
              <Input
                id="fecha_confirmacion"
                name="fecha_confirmacion"
                type="date"
                value={formData.fecha_confirmacion}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="lugar_confirmacion">Lugar de la Confirmación *</Label>
              <Input
                id="lugar_confirmacion"
                name="lugar_confirmacion"
                value={formData.lugar_confirmacion}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
          </CardContent>
        </Card>

        {/* Datos del Confirmado */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Datos del Confirmado
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="confirmado_nombre">Nombre *</Label>
              <Input
                id="confirmado_nombre"
                name="confirmado_nombre"
                value={formData.confirmado_nombre}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmado_apellido">Apellido *</Label>
              <Input
                id="confirmado_apellido"
                name="confirmado_apellido"
                value={formData.confirmado_apellido}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmado_fecha_nacimiento">Fecha de Nacimiento *</Label>
              <Input
                id="confirmado_fecha_nacimiento"
                name="confirmado_fecha_nacimiento"
                type="date"
                value={formData.confirmado_fecha_nacimiento}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmado_genero">Género *</Label>
              <Select
                value={formData.confirmado_genero}
                onValueChange={(value) => handleSelectChange("confirmado_genero", value)}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="masculino">Masculino</SelectItem>
                  <SelectItem value="femenino">Femenino</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="confirmado_lugar_nacimiento">Lugar de Nacimiento *</Label>
              <Input
                id="confirmado_lugar_nacimiento"
                name="confirmado_lugar_nacimiento"
                value={formData.confirmado_lugar_nacimiento}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
          </CardContent>
        </Card>

        {/* Datos del Bautizo */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Datos del Bautizo
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="parroquia_bautizo">Parroquia del Bautizo</Label>
              <Input
                id="parroquia_bautizo"
                name="parroquia_bautizo"
                value={formData.parroquia_bautizo}
                onChange={handleChange}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fecha_bautizo">Fecha del Bautizo</Label>
              <Input
                id="fecha_bautizo"
                name="fecha_bautizo"
                type="date"
                value={formData.fecha_bautizo}
                onChange={handleChange}
                className="h-11"
              />
            </div>
          </CardContent>
        </Card>

        {/* Padres y Padrinos */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Padres y Padrinos
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="padre_nombre">Nombre del Padre</Label>
              <Input
                id="padre_nombre"
                name="padre_nombre"
                value={formData.padre_nombre}
                onChange={handleChange}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="madre_nombre">Nombre de la Madre</Label>
              <Input
                id="madre_nombre"
                name="madre_nombre"
                value={formData.madre_nombre}
                onChange={handleChange}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="padrino_nombre">Nombre del Padrino</Label>
              <Input
                id="padrino_nombre"
                name="padrino_nombre"
                value={formData.padrino_nombre}
                onChange={handleChange}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="madrina_nombre">Nombre de la Madrina</Label>
              <Input
                id="madrina_nombre"
                name="madrina_nombre"
                value={formData.madrina_nombre}
                onChange={handleChange}
                className="h-11"
              />
            </div>
          </CardContent>
        </Card>

        {/* Obispo y Notas */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Obispo y Observaciones
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="obispo_nombre">Nombre del Obispo *</Label>
              <Input
                id="obispo_nombre"
                name="obispo_nombre"
                value={formData.obispo_nombre}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notas_marginales">Notas Marginales</Label>
              <Textarea
                id="notas_marginales"
                name="notas_marginales"
                value={formData.notas_marginales}
                onChange={handleChange}
                rows={4}
                className="resize-none"
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/confirmaciones")}
            className="h-11"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="h-11 bg-rose-950 hover:bg-rose-900 text-white"
            data-testid="guardar-confirmacion-btn"
          >
            {loading ? (
              <span className="animate-pulse">Guardando...</span>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? "Actualizar" : "Guardar"} Confirmación
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
